import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ProfileData {
  full_name: string | null;
  phone: string | null;
  location_city: string | null;
  bio: string | null;
  skills: string[] | null;
  experience_years: number | null;
}

interface CompletionItem {
  key: string;
  label: string;
  completed: boolean;
  action?: string;
}

export default function ProfileCompletion() {
  const navigate = useNavigate();
  const [completion, setCompletion] = useState(0);
  const [items, setItems] = useState<CompletionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkProfileCompletion();
  }, []);

  const checkProfileCompletion = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, phone, location_city, bio, skills, experience_years")
        .eq("id", user.id)
        .single();

      if (!profile) return;

      const completionItems: CompletionItem[] = [
        {
          key: "name",
          label: "Add your full name",
          completed: !!profile.full_name,
          action: "/subject-rights",
        },
        {
          key: "phone",
          label: "Add contact phone",
          completed: !!profile.phone,
          action: "/subject-rights",
        },
        {
          key: "location",
          label: "Add your location",
          completed: !!profile.location_city,
          action: "/subject-rights",
        },
        {
          key: "bio",
          label: "Write a professional bio",
          completed: !!profile.bio && profile.bio.length > 50,
          action: "/subject-rights",
        },
        {
          key: "skills",
          label: "Add your skills",
          completed: !!profile.skills && profile.skills.length > 0,
          action: "/subject-rights",
        },
        {
          key: "experience",
          label: "Add years of experience",
          completed: profile.experience_years !== null,
          action: "/subject-rights",
        },
      ];

      setItems(completionItems);

      const completedCount = completionItems.filter(item => item.completed).length;
      const completionPercentage = Math.round((completedCount / completionItems.length) * 100);
      setCompletion(completionPercentage);
    } catch (error) {
      console.error("Error checking profile completion:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  // Don't show if profile is 100% complete
  if (completion === 100) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Complete Your Profile</CardTitle>
        <CardDescription>
          A complete profile helps you stand out to employers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{completion}% Complete</span>
            <span className="text-muted-foreground">
              {items.filter(i => i.completed).length} of {items.length} completed
            </span>
          </div>
          <Progress value={completion} className="h-2" />
        </div>

        <div className="space-y-2">
          {items.slice(0, 3).map((item) => (
            <div
              key={item.key}
              className="flex items-center gap-2 text-sm"
            >
              {item.completed ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <span className={item.completed ? "text-muted-foreground line-through" : ""}>
                {item.label}
              </span>
            </div>
          ))}
          {items.length > 3 && (
            <p className="text-xs text-muted-foreground pl-6">
              +{items.length - 3} more items to complete
            </p>
          )}
        </div>

        <Button
          onClick={() => navigate("/subject-rights")}
          variant="outline"
          className="w-full"
          size="sm"
        >
          Complete Profile
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}
